import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { loginSchema } from '@fdt/shared';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import { errorKey } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { Logo } from '../components/Logo';
import { ThemeSwitcher } from '../components/ThemeSwitcher';

export function LoginPage() {
  const { t } = useTranslation();
  const { user, login } = useAuth();
  const location = useLocation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ username?: string; password?: string }>({});
  const [errorMessageKey, setErrorMessageKey] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (user) {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setErrorMessageKey(null);

    // Validate with the same schema the API uses.
    const parsed = loginSchema.safeParse({ username, password });
    if (!parsed.success) {
      const errors: { username?: string; password?: string } = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0];
        if (field === 'username' || field === 'password') {
          errors[field] = issue.message.startsWith('validation.')
            ? issue.message
            : 'validation.invalid';
        }
      }
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setSubmitting(true);
    try {
      await login(parsed.data);
    } catch (error) {
      setErrorMessageKey(errorKey(error));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        p: 2,
        bgcolor: 'background.default',
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 420, overflow: 'hidden' }}>
        {/* A band in the brand red: the one place the panel wears it at size,
            on the screen that is only ever this. */}
        <Box sx={{ height: 5, bgcolor: 'secondary.main' }} />
        <CardContent sx={{ p: 4 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Logo height={36} />
              <Typography variant="h1" gutterBottom sx={{ mt: 2.5 }}>
                {t('auth.loginTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('auth.loginSubtitle')}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <ThemeSwitcher />
              <LanguageSwitcher />
            </Stack>
          </Stack>

          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3 }}>
            <Stack spacing={2}>
              {errorMessageKey && <Alert severity="error">{t(errorMessageKey)}</Alert>}

              <TextField
                label={t('auth.username')}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                autoFocus
                fullWidth
                error={Boolean(fieldErrors.username)}
                helperText={fieldErrors.username ? t(fieldErrors.username) : undefined}
              />

              <TextField
                label={t('auth.password')}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                fullWidth
                error={Boolean(fieldErrors.password)}
                helperText={fieldErrors.password ? t(fieldErrors.password) : undefined}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {t('auth.submit')}
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
